// src/pages/private/TotalShopCommissionReport.jsx
import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Backdrop,
  Alert,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import BackButton from '../../components/BackButton';
// [CASH-MODE] UPI / Cash filter + indication
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, paymentModeLabel, filterRowsByMode, totalsByModeFromRows } from '../../utils/paymentMode';

/* ──────────────────────────────────────────────────────────────
 *  Helper – compute commission amount and shop commission for a single tx
 * ──────────────────────────────────────────────────────────── */
const computeCommissions = (tx) => {
  const amount = Number(tx.totalAmount ?? tx.transactionAmount ?? 0);
  const rate = Number(tx.commissionPercentage ?? tx.commissionRate ?? 0);
  
  // Calculate total commission amount
  const totalCommission = +(amount * rate * 0.01).toFixed(2);
  
  // Calculate shop commission = Total Amount - Total Commission
  const shopCommission = +(amount - totalCommission).toFixed(2);
  
  return {
    totalCommission,
    shopCommission
  };
};

const TotalShopCommissionReport = () => {
  const { shopId } = useParams();

  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({
    totalTransactions: 0,
    totalAmount: 0,
    totalCommissionAmount: 0,
    totalShopCommission: 0,
    averageCommissionPercentage: 0,
  });
  const [shopInfo, setShopInfo] = useState({
    businessName: '',
    shopVPA: '',
    city: '',
  });
  const [loading, setLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]

  const componentRef = useRef();

  // ────────────────────────────────────────────────────────────
  //  Auth token → userId
  // ────────────────────────────────────────────────────────────
  const token = sessionStorage.getItem('token');
  let userId = '';
  if (token) {
    try {
      userId = jwtDecode(token).userId;
    } catch (e) {
      console.error('Error decoding token:', e);
    }
  }

  /* ────────────────────────────────────────────────────────────
   *  Fetch data whenever dates | shopId change
   * ────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (shopId) {
      fetchCommissionData();
    } else {
      setDialogMessage('Shop ID is missing from the URL.');
      setOpenDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, shopId, paymentMode]); // [CASH-MODE] paymentMode

  /* ────────────────────────────────────────────────────────────
   *  Date helpers
   * ────────────────────────────────────────────────────────── */
  const handleStartDateChange = (date) => setStartDate(date);
  const handleEndDateChange   = (date) => setEndDate(date);
  const handleDialogClose     = () => setOpenDialog(false);

  const validateDates = () => {
    if (!startDate || !endDate) {
      setDialogMessage('Please select both start & end dates.');
      setOpenDialog(true);
      return false;
    }
    if (startDate > endDate) {
      setDialogMessage('Start date must be on or before end date.');
      setOpenDialog(true);
      return false;
    }
    if (
      (endDate - startDate) / (1000 * 60 * 60 * 24) > 31
    ) {
      setDialogMessage('Date range cannot exceed 31 days.');
      setOpenDialog(true);
      return false;
    }
    return true;
  };

  /* ────────────────────────────────────────────────────────────
   *  Fetch API
   * ────────────────────────────────────────────────────────── */
  const fetchCommissionData = async () => {
    if (!validateDates()) return;

    setLoading(true);

    try {
      const { data, status } = await axios.post(
        `${API_BASE_URL}/shopReport/totalShopCommissions`,
        {
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate:   format(endDate,   'yyyy-MM-dd'),
          shopId,
          userId,
          paymentMode: paymentModeParam(paymentMode), // [CASH-MODE] undefined for ALL
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (status === 200) {
        const {
          transactions: apiTransactions = [],
          totalTransactions: apiTotalTransactions = 0,
          shopInfo: apiShopInfo = {},
        } = data;
        // [CASH-MODE] safety net: keep only rows of the selected mode (no-op for ALL)
        const transactions = filterRowsByMode(apiTransactions, paymentMode);
        const totalTransactions = paymentModeParam(paymentMode) ? transactions.length : apiTotalTransactions;

        /* Map → rows and compute per-tx commission */
        let totalAmountSum = 0;
        let totalCommissionSum = 0;
        let totalShopCommissionSum = 0;
        let totalCommissionPercentage = 0;

        const mapped = transactions.map((tx, i) => {
          const amount = Number(tx.totalAmount ?? tx.transactionAmount ?? 0);
          const commissionPercentage = Number(tx.commissionPercentage ?? tx.commissionRate ?? 0);
          const { totalCommission, shopCommission } = computeCommissions(tx);

          // Accumulate totals for summary
          totalAmountSum += amount;
          totalCommissionSum += totalCommission;
          totalShopCommissionSum += shopCommission;
          totalCommissionPercentage += commissionPercentage;

          return {
            id: tx.id || tx._id,
            serialNumber: i + 1,
            transactionDate:
              tx.transactionDate || tx.createdAt || tx.date,
            customerName:  tx.customerName  || tx.payerName  || 'N/A',
            customerVPA:   tx.customerVPA   || tx.senderVpa  || 'N/A',
            totalAmount:   amount,
            commissionPercentage: commissionPercentage,
            totalCommission: totalCommission,
            shopCommission: shopCommission,
            transactionReferenceNumber:
              tx.transactionReferenceNumber ||
              tx.transactionId ||
              'N/A',
            paymentMode: tx.paymentMode, // [CASH-MODE]
          };
        });

        setTransactions(mapped);
        
        // Calculate averages and set summary
        const avgCommissionPercentage = totalTransactions > 0 ? 
          (totalCommissionPercentage / totalTransactions) : 0;

        setSummary({
          totalTransactions,
          totalAmount: totalAmountSum,
          totalCommissionAmount: totalCommissionSum,
          totalShopCommission: totalShopCommissionSum,
          averageCommissionPercentage: avgCommissionPercentage,
        });
        
        setShopInfo({
          businessName: apiShopInfo.businessName || '',
          shopVPA:      apiShopInfo.shopVPA      || '',
          city:         apiShopInfo.city         || '',
        });
      } else {
        throw new Error('Unexpected status code');
      }
    } catch (err) {
      console.error(err);
      setDialogMessage(
        err.response?.data?.message ||
          'Error fetching commission data.'
      );
      setOpenDialog(true);
      setTransactions([]);
      setSummary({
        totalTransactions: 0,
        totalAmount: 0,
        totalCommissionAmount: 0,
        totalShopCommission: 0,
        averageCommissionPercentage: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  /* ────────────────────────────────────────────────────────────
   *  Columns config
   * ────────────────────────────────────────────────────────── */
  const columns = [
    { field: 'serialNumber', headerName: 'S.No.', width: 70 },
    {
      field: 'transactionDate',
      headerName: 'Date',
      width: 110,
      renderCell: ({ value }) =>
        value ? format(new Date(value), 'dd-MM-yyyy') : 'N/A',
    },
    { field: 'customerVPA',  headerName: 'Customer VPA',  width: 180 },
    {
      field: 'totalAmount',
      headerName: 'Total Amount (₹)',
      width: 130,
      renderCell: ({ value }) => `₹${value.toFixed(2)}`,
    },
    {
      field: 'commissionPercentage',
      headerName: 'Commission %',
      width: 110,
      renderCell: ({ value }) => `${value.toFixed(2)}%`,
    },
    {
      field: 'totalCommission',
      headerName: 'Total Commission (₹)',
      width: 150,
      renderCell: ({ value }) => `₹${value.toFixed(2)}`,
    },
    {
      field: 'shopCommission',
      headerName: 'Shop Commission (₹)',
      width: 150,
      renderCell: ({ value }) => `₹${value.toFixed(2)}`,
      cellClassName: 'shop-commission-cell',
    },
    {
      field: 'transactionReferenceNumber',
      headerName: 'Transaction ID',
      width: 200,
    },
    // [CASH-MODE] UPI / Cash
    {
      field: 'paymentMode',
      headerName: 'Mode',
      width: 100,
      renderCell: (params) => <PaymentModeChip row={params.row} />,
    },
  ];

  /* ────────────────────────────────────────────────────────────
   *  Print handler
   * ────────────────────────────────────────────────────────── */
  const handlePrint = async () => {
    if (!transactions.length) {
      setDialogMessage('No commission data to print.');
      setOpenDialog(true);
      return;
    }
    try {
      const canvas = await html2canvas(componentRef.current, {
        scale: 2,
        useCORS: true,
        windowWidth: 1400,
      });
      const pdf  = new jsPDF('l', 'pt', 'a4');
      const imgW = pdf.internal.pageSize.getWidth();
      const imgH = (canvas.height * imgW) / canvas.width;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgW, imgH);
      pdf.save('TotalShopCommissionReport.pdf');
    } catch (e) {
      console.error(e);
      setDialogMessage('Error generating PDF.');
      setOpenDialog(true);
    }
  };

  const rows = transactions.map((tx) => ({
    id:               tx.id,
    serialNumber:     tx.serialNumber,
    transactionDate:  tx.transactionDate,
    customerVPA:      tx.customerVPA,
    totalAmount:      tx.totalAmount,
    commissionPercentage: tx.commissionPercentage,
    totalCommission:  tx.totalCommission,
    shopCommission:   tx.shopCommission,
    transactionReferenceNumber: tx.transactionReferenceNumber,
    paymentMode:      tx.paymentMode, // [CASH-MODE]
  }));

  const renderTableRows = () => {
    return rows.map((row) => (
      <tr key={row.id}>
        <td>{row.serialNumber}</td>
        <td>{row.transactionDate ? format(new Date(row.transactionDate), 'dd-MM-yyyy') : 'N/A'}</td>
        <td>{row.customerVPA}</td>
        <td>₹{row.totalAmount.toFixed(2)}</td>
        <td>{row.commissionPercentage.toFixed(2)}%</td>
        <td>₹{row.totalCommission.toFixed(2)}</td>
        <td style={{ fontWeight: 'bold', color: '#1976d2' }}>₹{row.shopCommission.toFixed(2)}</td>
        <td>{row.transactionReferenceNumber}</td>
        <td>{paymentModeLabel(row)}</td>{/* [CASH-MODE] */}
      </tr>
    ));
  };

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      {/* Place BackButton outside the Grid */}
      <BackButton />
      <br />  <br />
      
      {/* Custom CSS for Shop Commission column */}
      <style>
        {`
          .shop-commission-cell {
            font-weight: bold !important;
            color: #1976d2 !important;
            background-color: #f0f7ff !important;
          }
        `}
      </style>
      
      {/* Loading Backdrop */}
      <Backdrop 
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }} 
        open={loading}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" align="center">
          Processing Shop Commission Data...
        </Typography>
        <Typography variant="body2" align="center" sx={{ maxWidth: 400 }}>
          We're analyzing commission details for this shop. 
          This may take a few moments for large date ranges.
        </Typography>
      </Backdrop>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div style={{ padding: '20px' }}>
          <Typography variant="h6" style={{ marginTop: '10px' }}>
            Total Shop Commission Report
          </Typography>
          
          {/* Shop Info */}
          {shopInfo.businessName && (
            <Typography variant="body2" style={{ marginTop: '5px', color: 'gray' }}>
              Business: {shopInfo.businessName} | 
              VPA: {shopInfo.shopVPA} | 
              City: {shopInfo.city}
            </Typography>
          )}

      

          <Grid
            container
            spacing={2}
            alignItems="center"
            style={{ marginTop: '20px' }}
          >
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  fetchCommissionData();
                }}
                style={{ marginRight: '10px' }}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Submit'}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handlePrint}
                disabled={transactions.length === 0 || loading}
              >
                Print
              </Button>
            </Grid>
            {/* [CASH-MODE] Payment Mode filter */}
            <Grid item xs={12} sm={4}>
              <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
            </Grid>
          </Grid>

          {/* Summary Cards */}
          {transactions.length > 0 && (
            <Grid container spacing={2} style={{ marginTop: '20px' }}>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    padding: 2, 
                    textAlign: 'center',
                    height: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 180
                  }}
                >
                  <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Total Transactions
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {summary.totalTransactions}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    padding: 2, 
                    textAlign: 'center',
                    height: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 180
                  }}
                >
                  <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Total Amount
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                    ₹{summary.totalAmount.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    padding: 2, 
                    textAlign: 'center',
                    height: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 180
                  }}
                >
                  <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Avg Commission %
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {summary.averageCommissionPercentage.toFixed(2)}%
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    padding: 2, 
                    textAlign: 'center',
                    height: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 180
                  }}
                >
                  <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Total Commission (Platform Fee)
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                    ₹{summary.totalCommissionAmount.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    padding: 2, 
                    textAlign: 'center',
                    height: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 180,
                    backgroundColor: '#f0f7ff', 
                    border: '2px solid #1976d2',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(135deg, #f0f7ff 0%, #e3f2fd 100%)',
                      borderRadius: 1,
                      zIndex: -1
                    }
                  }}
                >
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: '#1976d2', 
                      fontWeight: 'bold', 
                      fontSize: '0.9rem' 
                    }}
                  >
                    Total Shop Commission
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      color: '#1976d2', 
                      fontWeight: 'bold', 
                      mt: 1
                    }}
                  >
                    ₹{summary.totalShopCommission.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* [CASH-MODE] UPI vs Cash split of the loaded rows */}
          {transactions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <PaymentModeTotals totals={totalsByModeFromRows(transactions, (t) => t.totalAmount)} amountKey="amount" title="Total Amount" />
              <PaymentModeTotals totals={totalsByModeFromRows(transactions, (t) => t.shopCommission)} amountKey="amount" title="Shop Commission" />
            </Box>
          )}

          {!loading && (
            <div style={{ marginTop: '20px', height: 600, width: '100%' }}>
              {transactions.length > 0 ? (
                <DataGrid
                  rows={rows}
                  columns={columns}
                  pageSize={25}
                  rowsPerPageOptions={[25, 50, 100]}
                  disableSelectionOnClick
                  loading={loading}
                  pagination
                  paginationMode="client"
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Typography variant="h6" align="center">
                    No commission data found for the selected dates.
                  </Typography>
                  {shopInfo.businessName && (
                    <Alert severity="info" sx={{ marginTop: 2, maxWidth: 600, margin: '16px auto' }}>
                      <Typography variant="body2">
                        <strong>Shop Info:</strong> {shopInfo.businessName} ({shopInfo.shopVPA}). 
                        Try expanding your date range to include more commission transactions.
                      </Typography>
                    </Alert>
                  )}
                </div>
              )}
            </div>
          )}

          <Dialog open={openDialog} onClose={handleDialogClose}>
            <DialogTitle>Message</DialogTitle>
            <DialogContent>
              <Typography>{dialogMessage}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} color="primary">
                OK
              </Button>
            </DialogActions>
          </Dialog>

          {/* Printable Component */}
          <div style={{ position: 'absolute', left: '-10000px', top: 0 }}>
            <div ref={componentRef} style={{ width: '1400px', padding: '20px' }}>
              <Typography variant="h4" align="center">
                Q-Pay Reports
              </Typography>
              <Typography
                variant="h6"
                align="center"
                style={{ marginTop: '10px' }}
              >
                Total Shop Commission Report
              </Typography>
              <Typography
                variant="subtitle1"
                align="center"
                style={{ marginBottom: '10px' }}
              >
                {`From ${format(startDate, 'dd-MM-yyyy')} to ${format(
                  endDate,
                  'dd-MM-yyyy'
                )}`}
              </Typography>
              {shopInfo.businessName && (
                <Typography
                  variant="subtitle2"
                  align="center"
                  style={{ marginBottom: '20px' }}
                >
                  Business: {shopInfo.businessName} | VPA: {shopInfo.shopVPA} | City: {shopInfo.city}
                </Typography>
              )}
              
              {/* Summary for Print */}
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <Typography variant="h6">Commission Summary</Typography>
                <Typography>Total Transactions: {summary.totalTransactions} | Total Amount: ₹{summary.totalAmount.toFixed(2)}</Typography>
                <Typography>Average Commission: {summary.averageCommissionPercentage.toFixed(2)}% | Total Commission (Platform Fee): ₹{summary.totalCommissionAmount.toFixed(2)}</Typography>
                <Typography style={{ fontWeight: 'bold', color: '#1976d2' }}>Total Shop Commission: ₹{summary.totalShopCommission.toFixed(2)}</Typography>
                {/* [CASH-MODE] */}
                <Typography>
                  {(() => {
                    const t = totalsByModeFromRows(transactions, (x) => x.totalAmount);
                    return `UPI: ₹${t.UPI.amount.toFixed(2)} (${t.UPI.count}) | Cash: ₹${t.CASH.amount.toFixed(2)} (${t.CASH.count})`;
                  })()}
                </Typography>
              </div>

              {transactions.length > 0 && (
                <table className="print-table" border="1" cellPadding="5">
                  <thead>
                    <tr>
                      <th>S.No.</th>
                      <th>Date</th>
                      <th>Customer VPA</th>
                      <th>Total Amount (₹)</th>
                      <th>Commission %</th>
                      <th>Total Commission (₹)</th>
                      <th style={{ backgroundColor: '#f0f7ff', fontWeight: 'bold' }}>Shop Commission (₹)</th>
                      <th>Transaction ID</th>
                      <th>Mode</th>{/* [CASH-MODE] */}
                    </tr>
                  </thead>
                  <tbody>
                    {renderTableRows()}
                  </tbody>
                </table>
              )}
              
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <Typography variant="h6" style={{ color: '#1976d2', fontWeight: 'bold' }}>
                  Grand Total Shop Commission: ₹{summary.totalShopCommission.toFixed(2)}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </LocalizationProvider>
    </Box>
  );
};

export default TotalShopCommissionReport;